import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Sparkles, Target, CheckCircle2, Activity, DollarSign, Settings2, AlertTriangle, Scale, Cpu, Users, Leaf, Trophy, Rocket, BarChart3 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { ASSESSMENT_MODULES } from "../data/assessments";
import { useAssessments } from "../context/AssessmentContext";

const ICONS = {
  "strategic": Activity,
  "financial": DollarSign,
  "operational": Settings2,
  "risk": AlertTriangle,
  "governance": Scale,
  "digital": Cpu,
  "human-capital": Users,
  "esg": Leaf,
  "market": Trophy,
  "growth": Rocket,
};

const AssessmentHub = () => {
  const { results } = useAssessments();
  const completedCount = Object.keys(results).length;

  return (
    <div data-testid="assessment-hub-page">
      <PageHeader
        eyebrow="Enterprise Health Assessment"
        title={<>Know where your organization <em className="text-brand-green font-serif italic">stands.</em></>}
        subtitle="Every organization has hidden strengths, operational risks, financial inefficiencies, and growth opportunities. Understand your maturity across critical business functions — before making strategic decisions."
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Key features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
            <div className="bg-white border border-black/5 p-6 rounded-sm flex gap-4">
              <Target className="h-6 w-6 text-brand-green flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-brand-ink">8-12 Minutes</div>
                <div className="text-sm text-brand-slate mt-1">Per module — quick yet rigorous.</div>
              </div>
            </div>
            <div className="bg-white border border-black/5 p-6 rounded-sm flex gap-4">
              <Sparkles className="h-6 w-6 text-brand-gold flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-brand-ink">Instant Executive Summary</div>
                <div className="text-sm text-brand-slate mt-1">Score, tier, and strategic recommendations.</div>
              </div>
            </div>
            <div className="bg-white border border-black/5 p-6 rounded-sm flex gap-4">
              <ShieldCheck className="h-6 w-6 text-brand-green flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-brand-ink">Confidential & Secure</div>
                <div className="text-sm text-brand-slate mt-1">Stored locally — never shared without consent.</div>
              </div>
            </div>
          </div>

          {/* Combined Growth Readiness Banner */}
          <div className="relative mb-16 overflow-hidden rounded-sm bg-brand-ink text-white p-8 md:p-12 grain">
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-brand-green/30 blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-brand-gold/20 blur-3xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-3">Business Growth Readiness Index</div>
                <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05]">Your combined dashboard — across <em className="text-brand-gold italic">all 10 modules.</em></h2>
                <p className="mt-4 text-white/70 max-w-xl">Completed <span className="text-brand-gold font-semibold">{completedCount} of 10</span> assessments. Unlock your executive-level scorecard with radar charts, benchmarks and strategic recommendations.</p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link to="/growth-readiness" data-testid="growth-readiness-cta" className="inline-flex items-center gap-2 bg-brand-gold text-brand-ink px-6 py-3.5 rounded-sm font-semibold hover:bg-white transition-colors">
                  <BarChart3 className="h-4 w-4" /> View Dashboard
                </Link>
              </div>
            </div>
          </div>

          {/* Modules Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ASSESSMENT_MODULES.map((m, i) => {
              const Icon = ICONS[m.slug] || Activity;
              const done = results[m.slug];
              return (
                <Link
                  key={m.slug}
                  to={`/assessment/${m.slug}`}
                  data-testid={`module-${m.slug}`}
                  className="group relative bg-white border border-black/5 p-7 rounded-sm hover:shadow-card-hover hover:-translate-y-1 transition-all overflow-hidden"
                >
                  <div className="absolute top-0 left-0 h-1 w-0 bg-brand-green group-hover:w-full transition-all duration-500" />
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 rounded-sm bg-brand-green-50 inline-flex items-center justify-center">
                      <Icon className="h-6 w-6 text-brand-green" />
                    </div>
                    <div className="text-xs tracking-[0.2em] uppercase text-brand-slate">0{i + 1}</div>
                  </div>
                  <h3 className="font-serif text-2xl text-brand-ink mt-6 leading-tight">{m.name}</h3>
                  <p className="text-sm text-brand-slate mt-3 leading-relaxed line-clamp-3">{m.headline}</p>

                  <div className="mt-6 pt-5 border-t border-black/5 flex items-center justify-between">
                    {done ? (
                      <div className="inline-flex items-center gap-2 text-xs text-brand-green font-semibold">
                        <CheckCircle2 className="h-4 w-4" /> Completed · {done.score?.pct}%
                      </div>
                    ) : (
                      <div className="text-xs text-brand-slate">8 questions · ~1 min</div>
                    )}
                    <ArrowUpRight className="h-4 w-4 text-brand-slate group-hover:text-brand-green group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AssessmentHub;
